import { useState, useRef, useMemo } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  TEMPLATE_CATEGORIES,
  MERGE_FIELDS_BY_CATEGORY,
  getTemplateCategory,
  type EmailTemplate,
  type CreateEmailTemplatePayload,
  type UpdateEmailTemplatePayload,
} from './hooks/useEmailTemplates';

interface EmailTemplateEditorProps {
  template: EmailTemplate | null;
  defaultType?: string | null;
  onClose: () => void;
}

/**
 * All available notification types grouped by category for the type selector.
 * These match the actual type strings used in notification queue calls across the API.
 */
const NOTIFICATION_TYPES: Record<string, string[]> = {
  auth: [
    'auth.account_locked',
    'auth.password_reset_requested',
    'auth.password_changed',
    'auth.mfa_enabled',
    'auth.mfa_disabled',
    'auth.login_new_device',
  ],
  user: [
    'user_invitation',
    'user.role_changed',
    'user.account_deactivated',
    'user.account_reactivated',
  ],
  contact: [
    'lead_stale_reminder',
    'unassigned_prospects_alert',
    'contact.lead_assigned',
    'contact.prospect_converted',
  ],
  job: [
    'job_overdue',
    'job_due_date_reminder',
    'task_assigned_to_client',
    'recurring_job_created',
    'reminder_due',
    'job.assigned',
    'job.status_changed',
    'job.completed',
  ],
  billing: [
    'invoice_overdue_reminder',
    'billing.invoice_sent',
    'billing.payment_received',
    'billing.quote_sent',
    'billing.recurring_invoice_generated',
  ],
  sla: ['sla_warning', 'sla_breach', 'sla_escalation'],
  document: [
    'document.request_created',
    'document.request_fulfilled',
    'document.request_overdue',
    'document_shared',
    'document_malware_detected',
  ],
  portal: [
    'portal_invitation',
    'portal_mfa_code',
    'portal.message_received',
    'portal.link_expiring',
    'portal.link_expired',
  ],
  system: [
    'system.report_ready',
    'system.tenant_provisioned',
    'system.bulk_operation_complete',
    'system.bulk_operation_failed',
  ],
};

/**
 * EmailTemplateEditor — modal for creating or editing an email template.
 * Features:
 * - Type/channel selection (create mode)
 * - Subject and body editing with merge field insertion
 * - Live preview panel showing rendered output with sample data
 * - Active/inactive toggle
 *
 * Requirements: 13.3 — Configurable email templates with merge fields
 */
export function EmailTemplateEditor({ template, defaultType, onClose }: EmailTemplateEditorProps) {
  const { toast } = useToast();
  const createMutation = useCreateEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  const isEditing = !!template;

  // ─── Form State ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    type: template?.type ?? defaultType ?? '',
    channel: template?.channel ?? ('email' as 'email' | 'in_app' | 'push'),
    subjectTemplate: template?.subjectTemplate ?? '',
    bodyTemplate: template?.bodyTemplate ?? '',
    mergeFields: template?.mergeFields ?? ([] as string[]),
    isActive: template?.isActive ?? true,
    triggerStatuses: template?.triggerStatuses ?? ([] as string[]),
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');
  const [insertTarget, setInsertTarget] = useState<'subject' | 'body'>('body');

  // ─── Derived ────────────────────────────────────────────────────────────────
  const category = getTemplateCategory(form.type);

  const availableMergeFields = useMemo(() => {
    if (!category) return [];
    return MERGE_FIELDS_BY_CATEGORY[category] || [];
  }, [category]);

  // Type select options grouped by category
  const typeOptions = useMemo(() => {
    const options = [{ value: '', label: 'Select notification type...' }];
    for (const [cat, types] of Object.entries(NOTIFICATION_TYPES)) {
      for (const type of types) {
        const label = type
          .split('.')
          .pop()!
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        options.push({
          value: type,
          label: `[${TEMPLATE_CATEGORIES[cat]}] ${label}`,
        });
      }
    }
    return options;
  }, []);

  const channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'in_app', label: 'In-App' },
    { value: 'push', label: 'Push' },
  ];

  // ─── Merge Field Insertion ──────────────────────────────────────────────────
  const insertMergeField = (field: string) => {
    const tag = `{{${field}}}`;

    if (insertTarget === 'subject') {
      const input = subjectRef.current;
      if (input) {
        const start = input.selectionStart ?? form.subjectTemplate.length;
        const end = input.selectionEnd ?? start;
        const newValue =
          form.subjectTemplate.slice(0, start) + tag + form.subjectTemplate.slice(end);
        setForm((prev) => ({ ...prev, subjectTemplate: newValue }));
        // Re-focus after state update
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + tag.length, start + tag.length);
        }, 0);
      }
    } else {
      const textarea = bodyRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? form.bodyTemplate.length;
        const end = textarea.selectionEnd ?? start;
        const newValue = form.bodyTemplate.slice(0, start) + tag + form.bodyTemplate.slice(end);
        setForm((prev) => ({ ...prev, bodyTemplate: newValue }));
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + tag.length, start + tag.length);
        }, 0);
      }
    }

    // Track used merge fields
    if (!form.mergeFields.includes(field)) {
      setForm((prev) => ({ ...prev, mergeFields: [...prev.mergeFields, field] }));
    }
  };

  // ─── Preview Rendering ──────────────────────────────────────────────────────
  const sampleData: Record<string, string> = useMemo(() => {
    const data: Record<string, string> = {
      'user.first_name': 'John',
      'user.last_name': 'Smith',
      'user.email': 'john@example.com',
      'client.first_name': 'Sarah',
      'client.last_name': 'Johnson',
      'client.email': 'sarah@company.com',
      'contact.first_name': 'Michael',
      'contact.last_name': 'Brown',
      'contact.email': 'michael@example.com',
      firm_name: 'Smith & Associates',
      reset_link: 'https://app.example.com/reset/abc123',
      login_url: 'https://app.example.com/login',
      invitation_link: 'https://app.example.com/invite/xyz',
      portal_link: 'https://portal.example.com',
      portal_url: 'https://portal.example.com/setup/abc123',
      payment_link: 'https://pay.example.com/inv/123',
      share_link: 'https://portal.example.com/shared/xyz',
      role_name: 'Senior Manager',
      assignee_name: 'Jane Doe',
      'job.title': 'FY2025 Tax Return',
      'job.name': 'FY2025 Tax Return',
      'job.reference': 'JOB-2025-001',
      'job.status': 'In Progress',
      'job.due_date': '15 Mar 2026',
      job_title: 'FY2025 Tax Return (JOB-2025-001)',
      task_title: 'Upload BAS Statement',
      title: 'Follow up with Sarah Johnson',
      'invoice.number': 'INV-2025-042',
      'invoice.amount': '$2,500.00',
      'invoice.due_date': '30 Sep 2025',
      'invoice.outstanding_balance': '$2,500.00',
      'invoice.days_overdue': '14',
      'quote.number': 'QTE-2025-010',
      'document.name': 'BAS_Q4_2025.pdf, Tax Return FY2025.pdf',
      due_date: '28 Feb 2026',
      notes: 'Please upload the original signed copy',
      document_count: '3',
      file_name: 'suspicious_file.exe',
      virus_name: 'Trojan.GenericKD.46542',
      expires_at: '7 Sep 2025',
      expiry_date: '7 Sep 2025',
      expiry_days: '7',
      code: '847293',
      expires_in_minutes: '10',
      stage: 'Qualified Lead',
      days_since_change: '14',
      count: '5',
      prospect_names: 'Michael Brown, Lisa Chen, David Park',
      target_hours: '48',
      elapsed_hours: '52.5',
      hours_since_breach: '4.5',
      escalation_level: '2',
      warning_threshold_percent: '80',
      'tenant.name': 'Acme Accounting',
      report_name: 'Monthly Revenue Report',
      admin_name: 'Platform Admin',
    };
    return data;
  }, []);

  const renderPreview = (text: string): string => {
    return text.replace(/\{\{(\s*[\w.]+\s*)\}\}/g, (_match, fieldPath: string) => {
      const trimmed = fieldPath.trim();
      const value = sampleData[trimmed];
      return value !== undefined
        ? `<span class="bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300 rounded px-1">${value}</span>`
        : `<span class="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded px-1">{{${trimmed}}}</span>`;
    });
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.type) {
      toast({ title: 'Please select a notification type', variant: 'error' });
      return;
    }
    if (!form.bodyTemplate.trim()) {
      toast({ title: 'Body template is required', variant: 'error' });
      return;
    }

    if (isEditing && template) {
      const data: UpdateEmailTemplatePayload = {
        subject_template: form.subjectTemplate || null,
        body_template: form.bodyTemplate,
        merge_fields: form.mergeFields,
        is_active: form.isActive,
        trigger_statuses: form.triggerStatuses.length > 0 ? form.triggerStatuses : null,
      };
      updateMutation.mutate(
        { id: template.id, data },
        {
          onSuccess: () => {
            toast({ title: 'Template updated', variant: 'success' });
            onClose();
          },
        },
      );
    } else {
      const data: CreateEmailTemplatePayload = {
        type: form.type,
        channel: form.channel,
        subject_template: form.subjectTemplate || null,
        body_template: form.bodyTemplate,
        merge_fields: form.mergeFields,
        is_active: form.isActive,
      };
      createMutation.mutate(data, {
        onSuccess: () => {
          toast({ title: 'Template created', variant: 'success' });
          onClose();
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : 'Failed to create template';
          toast({ title: message, variant: 'error' });
        },
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Edit Email Template' : 'New Email Template'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Type & Channel (only for new templates) */}
        {!isEditing && (
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Notification Type"
              options={typeOptions}
              value={form.type}
              onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              required
            />
            <Select
              label="Channel"
              options={channelOptions}
              value={form.channel}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  channel: e.target.value as 'email' | 'in_app' | 'push',
                }))
              }
              required
            />
          </div>
        )}

        {/* Type info (read-only in edit mode) */}
        {isEditing && template && (
          <div className="flex items-center gap-3 rounded-lg bg-surface-secondary px-4 py-2.5 dark:bg-surface-dark-tertiary">
            <span className="text-sm text-gray-500 dark:text-gray-400">Type:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {template.type}
            </span>
            <Badge
              variant={
                template.channel === 'email'
                  ? 'info'
                  : template.channel === 'in_app'
                    ? 'warning'
                    : 'default'
              }
              size="sm"
            >
              {template.channel === 'in_app' ? 'In-App' : template.channel}
            </Badge>
          </div>
        )}

        {/* Editor / Preview Tabs */}
        <div className="border-b border-border dark:border-border-dark">
          <div className="flex gap-4" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'editor'}
              className={`border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                activeTab === 'editor'
                  ? 'tab-active'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('editor')}
            >
              Editor
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'preview'}
              className={`border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                activeTab === 'preview'
                  ? 'tab-active'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('preview')}
            >
              Preview
            </button>
          </div>
        </div>

        {/* Editor Tab */}
        {activeTab === 'editor' && (
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label
                htmlFor="subject-template"
                className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Subject Template
              </label>
              <input
                ref={subjectRef}
                id="subject-template"
                type="text"
                className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                value={form.subjectTemplate}
                onChange={(e) => setForm((prev) => ({ ...prev, subjectTemplate: e.target.value }))}
                onFocus={() => setInsertTarget('subject')}
                placeholder="e.g., Job {{job.name}} - Status Update"
                maxLength={500}
              />
            </div>

            {/* Body */}
            <div>
              <label
                htmlFor="body-template"
                className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Body Template (HTML)
                <span className="ml-1 text-danger-500" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                ref={bodyRef}
                id="body-template"
                className="block w-full rounded-lg border border-border bg-white px-3 py-2 font-mono text-sm text-gray-900 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                rows={12}
                value={form.bodyTemplate}
                onChange={(e) => setForm((prev) => ({ ...prev, bodyTemplate: e.target.value }))}
                onFocus={() => setInsertTarget('body')}
                placeholder={
                  '<p>Hi {{client.first_name}},</p>\n<p>Your job {{job.name}} has been updated.</p>'
                }
                required
                maxLength={10000}
              />
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {form.bodyTemplate.length}/10,000 characters
              </p>
            </div>

            {/* Merge Fields Panel */}
            {form.type && availableMergeFields.length > 0 && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Available Merge Fields
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (click to insert into {insertTarget})
                  </span>
                </label>
                <div className="flex flex-wrap gap-1.5 rounded-lg border border-border bg-surface-secondary p-3 dark:border-border-dark dark:bg-surface-dark-tertiary">
                  {availableMergeFields.map((field) => (
                    <button
                      key={field}
                      type="button"
                      className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-900/20 dark:text-brand-300 dark:hover:bg-brand-900/40"
                      onClick={() => insertMergeField(field)}
                      title={`Insert {{${field}}}`}
                    >
                      {'{{'}
                      {field}
                      {'}}'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="peer sr-only"
                  role="switch"
                  aria-label="Template active status"
                />
                <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-600 peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-brand-500 peer-focus:ring-offset-1 dark:bg-gray-600 dark:peer-checked:bg-brand-500" />
              </label>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {form.isActive
                  ? 'Active — this template will be used for notifications'
                  : 'Inactive — system default will be used instead'}
              </span>
            </div>

            {/* Trigger Statuses */}
            {form.type.startsWith('billing.') && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Trigger on Statuses
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Select which status changes will trigger this email. Leave empty to trigger on
                  all.
                </p>
                <input
                  type="text"
                  value={form.triggerStatuses.join(', ')}
                  onChange={(e) => {
                    const statuses = e.target.value
                      .split(',')
                      .map((s) => s.trim().toLowerCase())
                      .filter(Boolean);
                    setForm((prev) => ({ ...prev, triggerStatuses: statuses }));
                  }}
                  placeholder="e.g. sent, accepted, declined"
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
            )}
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div className="space-y-4">
            {/* Subject preview */}
            {form.subjectTemplate && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Subject
                </label>
                <div
                  className="rounded-lg border border-border bg-white p-3 text-sm text-gray-900 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  dangerouslySetInnerHTML={{ __html: renderPreview(form.subjectTemplate) }}
                />
              </div>
            )}

            {/* Body preview */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Body
              </label>
              {form.bodyTemplate ? (
                <div
                  className="max-h-[400px] overflow-y-auto rounded-lg border border-border bg-white p-4 text-sm text-gray-900 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  dangerouslySetInnerHTML={{ __html: renderPreview(form.bodyTemplate) }}
                />
              ) : (
                <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  Enter body content in the Editor tab to see a preview.
                </p>
              )}
            </div>

            {/* Sample data legend */}
            <div className="rounded-lg bg-surface-secondary p-3 dark:bg-surface-dark-tertiary">
              <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                Preview uses sample data:
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {form.mergeFields.length > 0
                  ? form.mergeFields.map((field) => (
                      <span key={field} className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-mono text-gray-400">{`{{${field}}}`}</span>
                        {' = '}
                        <span className="font-medium">{sampleData[field] || '(no sample)'}</span>
                      </span>
                    ))
                  : availableMergeFields.slice(0, 6).map((field) => (
                      <span key={field} className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-mono text-gray-400">{`{{${field}}}`}</span>
                        {' = '}
                        <span className="font-medium">{sampleData[field] || '(no sample)'}</span>
                      </span>
                    ))}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-border pt-4 dark:border-border-dark">
          <div className="text-xs text-gray-400 dark:text-gray-500">
            {form.mergeFields.length > 0 && (
              <span>
                {form.mergeFields.length} merge field{form.mergeFields.length !== 1 ? 's' : ''} used
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSaving}
              disabled={!form.type || !form.bodyTemplate.trim()}
            >
              {isEditing ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
